import _ from 'lodash';
import React, { useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { Icon } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import { formatPeriod, pivotData } from './Pivot';
import { expandMoneyFields } from './utils/expandMoneyFields';

const S = {
  Row: styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0;
    opacity: 0;
    transition: opacity 150ms;
  `,

  ActionIcon: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border: none;
    border-radius: 0.25rem;
    background: none;
    padding: 0;
    color: ${({ $active, $danger }) => ($active ? ($danger ? colors.danger : colors.primary) : colors.grey40)};
    cursor: pointer;
    font-size: 0.6875rem;

    &:hover {
      background: none;
      color: ${colors.grey75};
    }
  `,
};

function tablePartToMarkdown(part) {
  const visibleFields = (part.fields || []).filter((f) => f.visible !== false);
  const exportFields = expandMoneyFields(visibleFields, part.data);
  if (!exportFields.length || !Array.isArray(part.data) || !part.data.length) return '';

  const header = exportFields.map((f) => f.label).join(' | ');
  const separator = exportFields.map((f) => (f.role === 'metric' ? '---:' : '---')).join(' | ');
  const rows = part.data.map((row) =>
    exportFields
      .map((f) => {
        const val = _.get(row, f.path);
        if (val == null) return '';
        if (Array.isArray(val)) return val.map((v) => (typeof v === 'object' ? (v?.name ?? '') : String(v))).join(', ');
        if (typeof val === 'object') return val.name ?? val.title ?? val.label ?? '';
        return String(val);
      })
      .join(' | '),
  );
  return `| ${header} |\n| ${separator} |\n${rows.map((r) => `| ${r} |`).join('\n')}`;
}

function pivotPartToMarkdown(part) {
  // Mirror the in-pivot "Save CSV" output: rows are dim tuples, columns are period labels,
  // cells are the raw cellField value. Tools that override cell rendering still get a
  // useful copy because the underlying numeric value lives on every data row.
  const pivot = pivotData({
    data: part.data,
    fields: part.fields,
    meta: part.meta,
    cellValue: part.cellValue,
    tooltipConfig: part.tooltipConfig,
  });
  if (!pivot) return '';

  const { rows, rowDimFields, periods, periodUnit, cellField } = pivot;
  const headers = [...rowDimFields.map((f) => f.label), ...periods.map((p) => formatPeriod(p, periodUnit))];
  const separator = [...rowDimFields.map(() => '---'), ...periods.map(() => '---:')];
  const bodyRows = rows.map((row) => {
    const dimCells = row.dimValues.map((v) => (v == null ? '' : String(v)));
    const periodCells = periods.map((p) => {
      const v = row.cells.get(p)?.[cellField.key];
      return v == null ? '' : String(v);
    });
    return [...dimCells, ...periodCells];
  });

  return [
    `| ${headers.join(' | ')} |`,
    `| ${separator.join(' | ')} |`,
    ...bodyRows.map((r) => `| ${r.join(' | ')} |`),
  ].join('\n');
}

function partsToText(parts) {
  return parts
    .map((p) => {
      if (p.type === 'text') return p.content;
      if (p.type === 'table') return tablePartToMarkdown(p);
      if (p.type === 'pivot') return pivotPartToMarkdown(p);
      return null;
    })
    .filter(Boolean)
    .join('\n\n');
}

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cellHtml(val) {
  if (val == null) return '';
  if (Array.isArray(val)) {
    return val.map((v) => (typeof v === 'object' ? escapeHtml(v?.name ?? '') : escapeHtml(String(v)))).join(', ');
  }
  if (typeof val === 'object') return escapeHtml(val.name ?? val.title ?? val.label ?? '');
  return escapeHtml(String(val));
}

function tablePartToHtml(part) {
  const visibleFields = (part.fields || []).filter((f) => f.visible !== false);
  const exportFields = expandMoneyFields(visibleFields, part.data);
  if (!exportFields.length || !Array.isArray(part.data) || !part.data.length) return '';

  const head = exportFields
    .map((f) => `<th style="text-align:${f.role === 'metric' ? 'right' : 'left'}">${escapeHtml(f.label)}</th>`)
    .join('');
  const body = part.data
    .map((row) => {
      const cells = exportFields
        .map((f) => {
          const align = f.role === 'metric' ? 'right' : 'left';
          return `<td style="text-align:${align}">${cellHtml(_.get(row, f.path))}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  return `<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function pivotPartToHtml(part) {
  const pivot = pivotData({
    data: part.data,
    fields: part.fields,
    meta: part.meta,
    cellValue: part.cellValue,
    tooltipConfig: part.tooltipConfig,
  });
  if (!pivot) return '';

  const { rows, rowDimFields, periods, periodUnit, cellField } = pivot;
  const headers = [
    ...rowDimFields.map((f) => `<th style="text-align:left">${escapeHtml(f.label)}</th>`),
    ...periods.map((p) => `<th style="text-align:right">${escapeHtml(formatPeriod(p, periodUnit))}</th>`),
  ].join('');
  const body = rows
    .map((row) => {
      const dimCells = row.dimValues
        .map((v) => `<td style="text-align:left">${v == null ? '' : escapeHtml(String(v))}</td>`)
        .join('');
      const periodCells = periods
        .map((p) => {
          const v = row.cells.get(p)?.[cellField.key];
          return `<td style="text-align:right">${v == null ? '' : escapeHtml(String(v))}</td>`;
        })
        .join('');
      return `<tr>${dimCells}${periodCells}</tr>`;
    })
    .join('');
  return `<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse"><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table>`;
}

function textPartToHtml(content) {
  // Reuse the same markdown pipeline as the chat surface (react-markdown + GFM)
  // so lists, links, emphasis, and tables survive a paste into Gmail/Word.
  return renderToStaticMarkup(<ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>);
}

function partsToHtml(parts) {
  const blocks = parts
    .map((p) => {
      if (p.type === 'text') return textPartToHtml(p.content);
      if (p.type === 'table') return tablePartToHtml(p);
      if (p.type === 'pivot') return pivotPartToHtml(p);
      return null;
    })
    .filter(Boolean);
  return `<div>${blocks.join('')}</div>`;
}

export default function ResponseActions({ message, conversationId }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const [feedback, setFeedback] = useState(message.feedback ?? null);
  const [copied, setCopied] = useState(false);

  const textContent = partsToText(message.parts);

  const handleFeedback = async (value) => {
    const newValue = feedback === value ? null : value;
    setFeedback(newValue);
    if (conversationId && message.id) {
      await api.www
        .workspaces(workspace.id)
        .ai()
        .conversations(conversationId)
        .messages(message.id)
        .updateFeedback({ feedback: newValue });
      if (newValue) toast.success('Thank you for your feedback!');
    }
  };

  const handleCopy = async () => {
    // Write both HTML and plain text so that pasting into Word, Gmail, Slack, etc.
    // produces formatted output, while plain-text editors still get the markdown.
    try {
      if (typeof ClipboardItem === 'function' && navigator.clipboard?.write) {
        const htmlContent = partsToHtml(message.parts);
        const item = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(textContent);
      }
    } catch {
      // Safari / older browsers: fall back to plain text only.
      try {
        await navigator.clipboard.writeText(textContent);
      } catch {
        /* clipboard unavailable — nothing else to try */
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <S.Row className="response-actions">
      <S.ActionIcon $active={feedback === 1} onClick={() => handleFeedback(1)} title="Helpful">
        <Icon icon="thumbs-up" />
      </S.ActionIcon>
      <S.ActionIcon $active={feedback === -1} $danger onClick={() => handleFeedback(-1)} title="Not helpful">
        <Icon icon="thumbs-down" />
      </S.ActionIcon>
      <S.ActionIcon onClick={handleCopy} title={copied ? 'Copied!' : 'Copy'}>
        <Icon icon={copied ? 'check' : 'clipboard'} />
      </S.ActionIcon>
      <S.ActionIcon onClick={handleDownload} title="Download">
        <Icon icon="download" />
      </S.ActionIcon>
    </S.Row>
  );
}
